import SellerOutboundReturnsApiV2 from 'api/v2/admins/seller-outbound-returns-api-v2'
import TitlePage from 'components/atoms/title-page'
import ConfirmationModal from 'components/organisms/confirmation-modal'
import { InputField } from 'components'
import { ALERT_TYPES } from 'constants/alert-constants'
import {
  CONSIGNOR_RETURN_CREATE_TYPE,
  CONSIGNOR_RETURN_SHIPMENT_TYPE,
  ITEM_STATUS,
  SHIPMENT_TYPE,
  STATUS
} from 'domains/seller-outbound-return/constants/seller-outbound-return-constants'
import { Form, Formik } from 'formik'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router'
import { Alert, Badge, Button, Card, CardBody, Col, Row, Table } from 'reactstrap'
import StringUtil from 'utils/string-util'

const propTypes = {
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const SELLER_RECEIVED_STATUS = STATUS.SELLER_RECEIVED || 'seller_received'

const ALLOWED_SHIPMENT_FILE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'application/pdf'
]

const MAX_SHIPMENT_FILE_SIZE = 5 * 1024 * 1024

const CONSIGNOR_RETURN_CREATE_TYPE_LIST = Object.values(CONSIGNOR_RETURN_CREATE_TYPE || {})
  .sort((firstOption, secondOption) => firstOption.label.localeCompare(secondOption.label))

const CONSIGNOR_RETURN_SHIPMENT_TYPE_LIST = Object.values(CONSIGNOR_RETURN_SHIPMENT_TYPE || {})

const CONSIGNOR_RETURN_ESTIMATED_SHIPPING_COST = {}
const displayValue = (value) => {
  if (value === null || value === undefined || value === '') return '-'

  return value
}

const formatLabel = (value) => {
  if (!value) return '-'

  return value
    .toString()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

const getEstimatedShippingCost = (shipmentType) => {
  if (!shipmentType) return ''

  return CONSIGNOR_RETURN_ESTIMATED_SHIPPING_COST[shipmentType] ?? ''
}

const formatEstimatedShippingCost = (value) => {
  if (value === '' || value === null || value === undefined) return '-'

  return StringUtil.currencyCase(String(Number(value || 0)))
}

const formatCreatedAt = (value) => {
  if (!value) return '-'

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  return date.toLocaleString('sv-SE', {
    timeZone : 'Asia/Jakarta',
    year     : 'numeric',
    month    : '2-digit',
    day      : '2-digit',
    hour     : '2-digit',
    minute   : '2-digit',
    hour12   : false
  })
}

const isSelfPickup = (shipmentType) => shipmentType === SHIPMENT_TYPE.SELF_PICKUP

const isSellerReturnItemCancelled = (item) => (
  item?.status === ITEM_STATUS.CANCELLED || item?.item_status === ITEM_STATUS.CANCELLED
)

const SellerOutboundReturnDetailPage = ({ pageUtils }) => {
  const { id } = useParams()
  const location = useLocation()

  const isProductCuratedRoute = location.pathname.startsWith('/app/product-curated-team')

  const [ sellerOutboundReturn, setSellerOutboundReturn ]           = useState({})
  const [ sellerOutboundReturnItems, setSellerOutboundReturnItems ] = useState([])
  const [ seller, setSeller ]                                       = useState({})
  const [ isLoading, setIsLoading ]                                 = useState(true)
  const [ submitError, setSubmitError ]                             = useState('')
  const [ isDownloadingPdf, setIsDownloadingPdf ]                   = useState(false)
  const [ cancelItemTarget, setCancelItemTarget ]                       = useState(null)
  const [ isCancelItemModalOpen, setIsCancelItemModalOpen ]             = useState(false)
  const [ cancelItemWarningStep, setCancelItemWarningStep ]             = useState(1)

  useEffect(() => {
    SellerOutboundReturnsApiV2.show(id)
      .then((response) => {
        const { seller_return } = response.data
        syncSellerReturnState(seller_return)

        setIsLoading(false)
      })
      .catch((error) => {
        const errorResponse = error?.response?.data || {
          messages: {
            base: error?.message || 'Unable to fetch consignor return detail'
          }
        }

        pageUtils?.setApiErrorMsg?.(errorResponse)
        pageUtils?.setAlertMsg?.(
          errorResponse?.messages?.base || 'Unable to fetch consignor return detail',
          ALERT_TYPES.DANGER
        )
        setIsLoading(false)
      })
  }, [ id, pageUtils ])

  const syncSellerReturnState = (sellerReturn) => {
    setSellerOutboundReturn(sellerReturn || {})
    setSellerOutboundReturnItems(sellerReturn?.items || [])
    setSeller(sellerReturn?.seller || {})
    setSubmitError('')
  }

  const downloadBlobResponse = (response, fallbackFilename, mimeType) => {
    const blob = new Blob([ response.data ], { type: mimeType })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    const contentDisposition = response.headers?.['content-disposition'] || ''
    const matchedFilename = contentDisposition.match(/filename="?([^";]+)"?/)

    link.href = url
    link.download = matchedFilename?.[1] || fallbackFilename
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  }

  const handleDownloadHandoverPdf = () => {
    setIsDownloadingPdf(true)

    SellerOutboundReturnsApiV2.downloadHandoverPdf(id)
      .then((response) => {
        downloadBlobResponse(
          response,
          `${sellerOutboundReturn?.code || 'consignor_return'}_handover_form.pdf`,
          'application/pdf'
        )
      })
      .catch((error) => {
        const errorResponse = error?.response?.data || {
          messages: {
            base: error?.message || 'Unable to download handover form'
          }
        }

        pageUtils?.setApiErrorMsg?.(errorResponse)
        pageUtils?.setAlertMsg?.(errorResponse?.messages?.base || 'Unable to download handover form', ALERT_TYPES.DANGER)
      })
      .finally(() => setIsDownloadingPdf(false))
  }

  const isReturnPending = () => sellerOutboundReturn.status === STATUS.PENDING

  const isReturnAccepted = () => sellerOutboundReturn.status === STATUS.ACCEPTED
  
  const isReturnReadyForShipment = () => isReturnPending() || isReturnAccepted()

  const isReturnScheduled = () => sellerOutboundReturn.status === STATUS.SCHEDULED

  const isReturnReceived = () => sellerOutboundReturn.status === SELLER_RECEIVED_STATUS

  const isProductCuratedFormEditable = () => (
    [
      STATUS.PENDING,
      STATUS.ACCEPTED
    ].includes(sellerOutboundReturn.status)
  )

  const formatShipmentTypeLabel = (value) => {
    const option = CONSIGNOR_RETURN_SHIPMENT_TYPE_LIST.find(
      (shipmentType) => shipmentType.value === value
    )

    return option?.label || formatLabel(value)
  }

  const formatReturnTypeLabel = (value) => {
    const option = CONSIGNOR_RETURN_CREATE_TYPE_LIST.find(
      (returnType) => returnType.value === value
    )

    return option?.label || formatLabel(value)
  }

  const formatStatusLabel = (status) => {
    const statusMap = {
      [STATUS.PENDING]         : 'Pending',
      [STATUS.ACCEPTED] : 'Ready for Outbound',
      [STATUS.SCHEDULED]       : 'Shipped',
      [SELLER_RECEIVED_STATUS] : 'Received by Consignor'
    }

    return statusMap[status] || formatLabel(status)
  }

  const statusColor = (status) => {
    const colorMap = {
      [STATUS.PENDING]         : 'warning',
      [STATUS.ACCEPTED]        : 'info',
      [STATUS.SCHEDULED]       : 'primary',
      [SELLER_RECEIVED_STATUS] : 'success'
    }

    return colorMap[status] || 'secondary'
  }

  const selectedUserDetail = () => (
    sellerOutboundReturn?.user_detail ||
    sellerOutboundReturn?.consignor_detail ||
    sellerOutboundReturn?.seller?.user_detail ||
    sellerOutboundReturn?.seller?.detail ||
    null
  )

  const selectedAddressDetail = () => (
    sellerOutboundReturn?.address_detail ||
    sellerOutboundReturn?.selected_address ||
    sellerOutboundReturn?.seller?.address_detail ||
    null
  )

  const compactAddress = (address) => {
    if (!address) return ''

    const directAddress = address.complete_address || address.street_address
    if (directAddress) return directAddress

    return [
      address.sub_district,
      address.district,
      address.city,
      address.province,
      address.postal_code
    ].filter(Boolean).join(', ')
  }

  const fullAddress = () => {
    const selectedAddressText = compactAddress(selectedAddressDetail())
    if (selectedAddressText) return selectedAddressText

    const addressParts = [
      sellerOutboundReturn.street_address,
      sellerOutboundReturn.sub_district,
      sellerOutboundReturn.district,
      sellerOutboundReturn.city,
      sellerOutboundReturn.province,
      sellerOutboundReturn.postal_code
    ].filter(Boolean)

    return addressParts.length > 0
      ? addressParts.join(', ')
      : '-'
  }

  const consignorName = () => (
    selectedUserDetail()?.name ||
    selectedUserDetail()?.formatted_name ||
    sellerOutboundReturn?.seller?.name ||
    sellerOutboundReturn?.seller_name ||
    seller?.name ||
    '-'
  )

  const recipientPhone = () => (
    selectedUserDetail()?.phone ||
    selectedUserDetail()?.phone_number ||
    selectedAddressDetail()?.telephone ||
    selectedAddressDetail()?.recipient_number ||
    sellerOutboundReturn?.seller?.phone ||
    sellerOutboundReturn?.seller?.mobile ||
    sellerOutboundReturn?.seller?.phone_number ||
    seller?.phone ||
    seller?.mobile ||
    seller?.phone_number ||
    seller?.user?.phone ||
    seller?.user?.mobile ||
    '-'
  )

  const getReturnTypeOptions = () => {
    const currentReturnType = sellerOutboundReturn?.return_type
    const hasCurrentReturnType = CONSIGNOR_RETURN_CREATE_TYPE_LIST.some(
      (option) => option.value === currentReturnType
    )

    if (!currentReturnType || hasCurrentReturnType) return CONSIGNOR_RETURN_CREATE_TYPE_LIST

    return [
      {
        value : currentReturnType,
        label : `${formatLabel(currentReturnType)} (Legacy)`
      },
      ...CONSIGNOR_RETURN_CREATE_TYPE_LIST
    ]
  }

  const getShipmentTypeOptions = () => {
    const currentShipmentType = sellerOutboundReturn?.shipment_type
    const hasCurrentShipmentType = CONSIGNOR_RETURN_SHIPMENT_TYPE_LIST.some(
      (option) => option.value === currentShipmentType
    )

    if (!currentShipmentType || hasCurrentShipmentType) return CONSIGNOR_RETURN_SHIPMENT_TYPE_LIST

    return [
      {
        value : currentShipmentType,
        label : `${formatShipmentTypeLabel(currentShipmentType)} (Legacy)`
      },
      ...CONSIGNOR_RETURN_SHIPMENT_TYPE_LIST
    ]
  }

  const missingSourceLocationItems = sellerOutboundReturnItems.filter(
    (item) => !isSellerReturnItemCancelled(item) && (item?.source_location_status === 'missing' || item?.inventory?.source_location_status === 'missing')
  )

  const hasMissingSourceLocation = missingSourceLocationItems.length > 0

  const missingSourceLocationSkus = missingSourceLocationItems
    .map((item) => item?.inventory?.inventory_sku || item?.productable?.sku || item?.sku)
    .filter(Boolean)

  const shipBlockedMessage = hasMissingSourceLocation
    ? `Unable to ship this Consignor Return because source location is missing for SKU: ${missingSourceLocationSkus.join(', ')}. Please check Inventory List first.`
    : ''

  const validateShipmentFile = (file, label) => {
    if (!file) return null

    const fileName = file.name || ''
    const isAllowedExtension = /\.(jpg|jpeg|png|pdf)$/i.test(fileName)
    const isAllowedMimeType = ALLOWED_SHIPMENT_FILE_TYPES.includes(file.type)

    if (!isAllowedMimeType && !isAllowedExtension) {
      return `${label} must be JPG, PNG, or PDF`
    }

    if (file.size > MAX_SHIPMENT_FILE_SIZE) {
      return `${label} maximum size is 5MB`
    }

    return null
  }

  const validateOutboundShipmentForm = (values, formikBag) => {
    const errors = {}

    const shipmentReceiptError = validateShipmentFile(values.shipment_receipt_file, 'Resi file')
    const shipmentProofError = validateShipmentFile(values.shipment_proof_file, 'Shipment proof file')

    if (shipmentReceiptError) errors.shipment_receipt_file = shipmentReceiptError
    if (shipmentProofError) errors.shipment_proof_file = shipmentProofError

    if (Object.keys(errors).length > 0) {
      formikBag.setErrors(errors)
      formikBag.setSubmitting(false)
      return false
    }

    return true
  }

  const handleApiSubmitError = (error, fallbackMessage, formikBag) => {
    const errorResponse = error?.response?.data || {
      messages: {
        base: error?.message || fallbackMessage
      }
    }

    const message = errorResponse?.messages?.base || fallbackMessage

    setSubmitError(message)
    pageUtils?.setApiErrorMsg?.(errorResponse)
    pageUtils?.setAlertMsg?.(message, ALERT_TYPES.DANGER)
    formikBag?.setErrors?.(errorResponse?.messages || { base: message })
  }

  const handleSubmitProductCuratedDetail = (values, formikBag) => {
    setSubmitError('')

    const payload = {
      ...values,
      estimated_shipping_cost: values.estimated_shipping_cost
    }

    SellerOutboundReturnsApiV2.update({
      id,
      values: payload
    })
      .then((response) => {
        const { seller_return } = response.data
        syncSellerReturnState(seller_return)

        pageUtils?.setAlertMsg?.('Consignor Return detail has been updated', ALERT_TYPES.SUCCESS)
      })
      .catch((error) => {
        handleApiSubmitError(error, 'Unable to update Consignor Return detail', formikBag)
      })
      .finally(() => formikBag.setSubmitting(false))
  }

  const handleSubmitOutboundShipment = (values, formikBag) => {
    setSubmitError('')

    if (isReturnReadyForShipment()) {

      if (hasMissingSourceLocation) {
        setSubmitError(shipBlockedMessage)
        pageUtils?.setAlertMsg?.(shipBlockedMessage, ALERT_TYPES.DANGER)
        formikBag.setErrors({ base: shipBlockedMessage })
        formikBag.setSubmitting(false)
        return
      }

      if (!validateOutboundShipmentForm(values, formikBag)) return

      SellerOutboundReturnsApiV2.ship({
        id,
        values: {
          tracking_no           : values.tracking_no,
          shipment_receipt_file : values.shipment_receipt_file,
          shipment_proof_file   : values.shipment_proof_file
        }
      })
        .then((response) => {
          const { seller_return } = response.data
          syncSellerReturnState(seller_return)

          pageUtils?.setAlertMsg?.('Consignor Return has been shipped', ALERT_TYPES.SUCCESS)
          formikBag.resetForm()
        })
        .catch((error) => {
          handleApiSubmitError(error, 'Unable to ship Consignor Return', formikBag)
        })
        .finally(() => formikBag.setSubmitting(false))

      return
    }

    formikBag.setSubmitting(false)
  }

  const handleReceive = () => {
    SellerOutboundReturnsApiV2.receive({ id })
      .then((response) => {
        const { seller_return } = response.data
        syncSellerReturnState(seller_return)

        pageUtils?.setAlertMsg?.('Consignor Return has been marked as received by consignor', ALERT_TYPES.SUCCESS)
      })
      .catch((error) => {
        const errorResponse = error?.response?.data || {
          messages: {
            base: error?.message || 'Unable to mark consignor return as received'
          }
        }

        const message = errorResponse?.messages?.base || 'Unable to mark consignor return as received'

        pageUtils?.setApiErrorMsg?.(errorResponse)
        pageUtils?.setAlertMsg?.(message, ALERT_TYPES.DANGER)
      })
  }

  const ReadOnlyField = ({ label, value, children, multiline }) => (
    <div className='mb-3'>
      <label className='text-gray-light'>
        <strong>{ label }</strong>
      </label>
      <div
        className='form-control bg-light'
        style={
          multiline
            ? {
              minHeight  : '80px',
              height     : 'auto',
              whiteSpace : 'normal'
            }
            : undefined
        }
      >
        { children || displayValue(value) }
      </div>
    </div>
  )

  ReadOnlyField.propTypes = {
    label     : PropTypes.string,
    value     : PropTypes.oneOfType([ PropTypes.string, PropTypes.number ]),
    children  : PropTypes.node,
    multiline : PropTypes.bool
  }

  const StatusReadOnlyField = () => (
    <ReadOnlyField label='Status'>
      <Badge color={ statusColor(sellerOutboundReturn?.status) }>
        { formatStatusLabel(sellerOutboundReturn?.status) }
      </Badge>
    </ReadOnlyField>
  )

  const itemProductSku = (item) => (
    item?.productable?.sku ||
    item?.inventory?.product_sku ||
    item?.inventory?.inventory_sku ||
    item?.sku ||
    '-'
  )

  const itemProductName = (item) => (
    item?.productable?.name ||
    item?.inventory?.product_name ||
    item?.product?.name ||
    '-'
  )

  const itemProductPhotoUrl = (item) => {
    const possibleUrls = [
      item?.productable?.photo_url,
      item?.productable?.main_image_url,
      item?.productable?.product_photo_url,
      item?.productable?.product_image_url,
      item?.productable?.product_photo_urls,
      item?.productable?.product_image_links,
      item?.productable?.photo_links,
      item?.inventory?.product_photo_url,
      item?.inventory?.main_image_url,
      item?.inventory?.product_photo_urls,
      item?.inventory?.product_image_links
    ].flat().filter(Boolean)

    return possibleUrls[0] || ''
  }

  const isShipmentItemCancel = sellerOutboundReturn?.status === STATUS.SCHEDULED

  const canCancelSellerReturnItem = (item) => (
    isProductCuratedRoute &&
    [ STATUS.PENDING, STATUS.ACCEPTED, STATUS.SCHEDULED ].includes(sellerOutboundReturn?.status) &&
    !isSellerReturnItemCancelled(item)
  )

  const handleCancelItemClick = (item) => {
    setCancelItemTarget(item)
    setCancelItemWarningStep(1)
    setIsCancelItemModalOpen(true)
  }

  const handleCancelItemModalToggle = () => {
    setIsCancelItemModalOpen((currentState) => {
      if (currentState) {
        setCancelItemTarget(null)
        setCancelItemWarningStep(1)
      }

      return !currentState
    })
  }

  const handleCancelItemAbort = () => {
    const code = String(sellerOutboundReturn?.code || '-').replace(/^#/, '')
    const sku = itemProductSku(cancelItemTarget) || '-'

    pageUtils?.setAlertMsg?.(
      `Cancel item SKU ${sku} dari Consignor Return #${code} dibatalkan.`,
      ALERT_TYPES.DANGER
    )
  }

  const handleCancelItemConfirm = () => {
    if (isShipmentItemCancel && cancelItemWarningStep < 3) {
      setCancelItemWarningStep((currentStep) => currentStep + 1)
      return
    }

    const itemId = cancelItemTarget?.id

    if (!itemId) return

    SellerOutboundReturnsApiV2.cancelItem({ id, itemId })
      .then((response) => {
        const parentSellerReturn = response.data.parent_seller_return

        if (parentSellerReturn) {
          syncSellerReturnState(parentSellerReturn)
        } else {
          setSellerOutboundReturnItems((currentItems) => currentItems.map((item) => (
            item.id === itemId
              ? { ...item, status: ITEM_STATUS.CANCELLED }
              : item
          )))
        }

        pageUtils?.setAlertMsg?.(
          `SKU ${itemProductSku(cancelItemTarget)} successfully cancelled from Consignor Return`,
          ALERT_TYPES.SUCCESS
        )
        setIsCancelItemModalOpen(false)
        setCancelItemTarget(null)
        setCancelItemWarningStep(1)
      })
      .catch((error) => {
        const errorResponse = error?.response?.data || {
          messages: {
            base: error?.message || 'Unable to cancel consignor return item'
          }
        }

        pageUtils?.setApiErrorMsg?.(errorResponse)
        pageUtils?.setAlertMsg?.(
          errorResponse?.messages?.base || 'Unable to cancel consignor return item',
          ALERT_TYPES.DANGER
        )
      })
  }

  const cancelItemModalTitle = isShipmentItemCancel
    ? `Warning ${cancelItemWarningStep}/3 - Cancel Shipped Consignor Return Item`
    : 'Cancel Consignor Return Item'

  const cancelItemModalMessage = (() => {
    const code = sellerOutboundReturn?.code || ''
    const sku = itemProductSku(cancelItemTarget)

    if (!isShipmentItemCancel) {
      return `Are you sure you want to cancel SKU ${sku} from Consignor Return ${code}? Only this item will be cancelled.`
    }

    if (cancelItemWarningStep === 1) {
      return `WARNING 1/3: SKU ${sku} from Consignor Return ${code} is already Shipped. Only this item will be cancelled.`
    }

    if (cancelItemWarningStep === 2) {
      return 'WARNING 2/3: Shipment inventory for this item will be reversed back to its previous warehouse location before the item is cancelled.'
    }

    return 'WARNING 3/3: This action changes inventory after shipment. Do not continue if this item has actually been received by the consignor. Are you absolutely sure you want to cancel this item?'
  })()

  const ProductTableReadOnly = () => (
    <>
      <hr />

      <h3 className='mb-3'>{ 'Product Table' }</h3>

      <Alert color='info' className='mb-3'>
        { 'Item Status menunjukkan status SKU di dalam batch Consignor Return. Active = SKU masih diproses dalam batch; Cancelled = SKU sudah dibatalkan dari batch.' }
      </Alert>

      {
        isProductCuratedRoute &&
        (
          <Alert color='warning' className='mb-3'>
            <strong>{ 'Catatan pembatalan:' }</strong>
            { ' tombol Cancel Item di halaman Detail hanya membatalkan SKU/item yang dipilih. Untuk membatalkan seluruh SKU/item aktif dalam satu Consignor Return (CSR), kembali ke halaman List lalu gunakan tombol Cancel pada CSR tersebut.' }
          </Alert>
        )
      }

      <Table bordered responsive>
        <thead>
          <tr>
            <th>{ 'No.' }</th>
            <th>{ 'Foto Product' }</th>
            <th>{ 'SKU Product' }</th>
            <th>{ 'Product Name' }</th>
            <th>{ 'Item Status' }</th>
            { isProductCuratedRoute && <th>{ 'Action' }</th> }
          </tr>
        </thead>
        <tbody>
          {
            sellerOutboundReturnItems.length === 0
              ? (
                <tr>
                  <td colSpan={ isProductCuratedRoute ? 6 : 5 } className='text-center text-muted'>
                    { 'Tidak ada product pada Consignor Return ini.' }
                  </td>
                </tr>
              )
              : sellerOutboundReturnItems.map((item, index) => {
                const photoUrl = itemProductPhotoUrl(item)
                const sku = itemProductSku(item)
                const isCancelled = isSellerReturnItemCancelled(item)

                return (
                  <tr key={ item.id || `${sku}-${index}` }>
                    <td>{ index + 1 }</td>
                    <td>
                      {
                        photoUrl
                          ? (
                            <a href={ photoUrl } target='_blank' rel='noreferrer'>
                              <img
                                src={ photoUrl }
                                alt={ sku }
                                style={{
                                  width        : '72px',
                                  height       : '72px',
                                  objectFit    : 'cover',
                                  borderRadius : '6px'
                                }}
                              />
                            </a>
                          )
                          : '-'
                      }
                    </td>
                    <td>{ sku }</td>
                    <td>{ itemProductName(item) }</td>
                    <td>
                      <Badge color={ isCancelled ? 'danger' : 'success' }>
                        { formatLabel(item.status || ITEM_STATUS.ACTIVE) }
                      </Badge>
                    </td>
                    {
                      isProductCuratedRoute &&
                      (
                        <td>
                          <Button
                            color='danger'
                            type='button'
                            size='sm'
                            disabled={ !canCancelSellerReturnItem(item) }
                            onClick={ () => handleCancelItemClick(item) }
                          >
                            { isCancelled ? 'Cancelled' : 'Cancel Item' }
                          </Button>
                        </td>
                      )
                    }
                  </tr>
                )
              })
          }
        </tbody>
      </Table>
    </>
  )

  const ConsignorInformationReadOnly = ({ showShipmentType = true }) => (
    <>
      <hr />

      <h3 className='mb-3'>{ 'Consignor Information' }</h3>

      <ReadOnlyField
        label='Consignor'
        value={ seller?.email || sellerOutboundReturn?.seller?.email || '-' }
      />

      <ReadOnlyField
        label='User Detail'
        value={ consignorName() }
      />

      <ReadOnlyField
        label='Phone Number'
        value={ recipientPhone() }
      />

      <ReadOnlyField
        label='Alamat Detail'
        value={ fullAddress() }
        multiline={ true }
      />

      {
        showShipmentType &&
        (
          <ReadOnlyField
            label='Shipment Type'
            value={ formatShipmentTypeLabel(sellerOutboundReturn?.shipment_type) }
          />
        )
      }

      <ReadOnlyField
        label='Estimasi Ongkir'
        value={
          formatEstimatedShippingCost(sellerOutboundReturn?.estimated_shipping_cost)
        }
      />

    </>
  )

  ConsignorInformationReadOnly.propTypes = {
    showShipmentType: PropTypes.bool
  }

  const ProductCuratedConsignorReturnDetailForm = () => (
    <Formik
      enableReinitialize={ true }
      initialValues={
        {
          return_type             : sellerOutboundReturn?.return_type || '',
          shipment_type           : sellerOutboundReturn?.shipment_type || '',
          estimated_shipping_cost : sellerOutboundReturn?.estimated_shipping_cost ?? '',
          user_detail_id          : sellerOutboundReturn?.user_detail_id || selectedUserDetail()?.id || '',
          address_id              : sellerOutboundReturn?.address_id || selectedAddressDetail()?.id || '',
          street_address          : fullAddress() === '-' ? '' : fullAddress()
        }
      }
      onSubmit={ handleSubmitProductCuratedDetail }
    >
      {
        (formikBag) => {
          const handleChangeShipmentType = (event) => {
            const nextShipmentType = event.target.value

            formikBag.setFieldValue('shipment_type', nextShipmentType)
            formikBag.setFieldValue('estimated_shipping_cost', isSelfPickup(nextShipmentType) ? 0 : '')
          }

          return (
            <Form>
              <ReadOnlyField
                label='Consignor Return Code'
                value={ sellerOutboundReturn?.code }
              />

              <ReadOnlyField
                label='Created At'
                value={ formatCreatedAt(sellerOutboundReturn?.created_at) }
              />

              <StatusReadOnlyField />

              <ReadOnlyField
                label='Consignor'
                value={ seller?.email || sellerOutboundReturn?.seller?.email || '-' }
              />

              <ReadOnlyField
                label='User Detail'
                value={ consignorName() }
              />

              <ReadOnlyField
                label='Phone Number'
                value={ recipientPhone() }
              />

              <ReadOnlyField
                label='Alamat Detail'
                value={ fullAddress() }
                multiline={ true }
              />

              <InputField
                type='select'
                name='return_type'
                label='Return Type'
                disabled={ !isProductCuratedFormEditable() || formikBag.isSubmitting }
              >
                <option value=''>{ 'Select Return Type' }</option>
                {
                  getReturnTypeOptions().map((option) => (
                    <option key={ option.value } value={ option.value }>
                      { option.label }
                    </option>
                  ))
                }
              </InputField>

              <InputField
                type='select'
                name='shipment_type'
                label='Shipment Type'
                required={ true }
                onChange={ handleChangeShipmentType }
                disabled={ !isProductCuratedFormEditable() || formikBag.isSubmitting }
              >
                <option value=''>{ 'Select Shipment Type' }</option>
                {
                  getShipmentTypeOptions().map((option) => (
                    <option key={ option.value } value={ option.value }>
                      { option.label }
                    </option>
                  ))
                }
              </InputField>

              <ReadOnlyField
                label='Estimasi Ongkir'
                value={ formatEstimatedShippingCost(formikBag.values.estimated_shipping_cost) }
              />

              {
                isSelfPickup(formikBag.values.shipment_type) &&
                (
                  <Alert color='info' className='mt-3'>
                    { 'Self Pickup tidak membutuhkan alamat pengiriman.' }
                  </Alert>
                )
              }

              <ProductTableReadOnly />

              {
                !isProductCuratedFormEditable() &&
                (
                  <Alert color='info' className='mt-3'>
                    { 'Consignor Return detail cannot be edited after shipment process has started.' }
                  </Alert>
                )
              }

              {
                submitError &&
                (
                  <Alert color='danger' className='mt-3'>
                    <strong>{ 'Unable to continue.' }</strong>
                    <div>{ submitError }</div>
                  </Alert>
                )
              }

              <div className='d-flex justify-content-start mt-4'>
                <Button
                  color='primary'
                  type='submit'
                  disabled={ !isProductCuratedFormEditable() || formikBag.isSubmitting }
                >
                  { formikBag.isSubmitting ? 'Saving...' : 'Save Changes' }
                </Button>
              </div>
            </Form>
          )
        }
      }
    </Formik>
  )

  const OutboundConsignorReturnDetailForm = () => (
    <Formik
      enableReinitialize={ true }
      initialValues={
        {
          tracking_no           : sellerOutboundReturn?.tracking_no || '',
          shipment_receipt_file : null,
          shipment_proof_file   : null
        }
      }
      onSubmit={ handleSubmitOutboundShipment }
    >
      {
        (formikBag) => (
          <Form>
            <ReadOnlyField
              label='Consignor Return Code'
              value={ sellerOutboundReturn?.code }
            />

            <ReadOnlyField
              label='Created At'
              value={ formatCreatedAt(sellerOutboundReturn?.created_at) }
            />

            <StatusReadOnlyField />

            <ReadOnlyField
              label='Return Type'
              value={ formatReturnTypeLabel(sellerOutboundReturn?.return_type) }
            />

            <ProductTableReadOnly />

            <ConsignorInformationReadOnly />

            <hr />

            <h3 className='mb-3'>{ 'Shipment Operation' }</h3>

            {
              isReturnPending() &&
              (
                <Alert color='info'>
                  { 'Consignor Return request sudah otomatis diterima. Outbound bisa langsung lanjut packing dan shipping.' }
                </Alert>
              )
            }

            {
              isReturnReadyForShipment() &&
              (
                <>
                  {
                    hasMissingSourceLocation &&
                    (
                      <Alert color='warning'>
                        <strong>{ 'Source location belum siap untuk Ship.' }</strong>
                        <div>{ shipBlockedMessage }</div>
                      </Alert>
                    )
                  }

                  <InputField
                    type='text'
                    name='tracking_no'
                    label='Tracking No.'
                    placeholder='Input tracking number / resi'
                    disabled={ formikBag.isSubmitting || hasMissingSourceLocation }
                  />

                  <div className='form-group'>
                    <label>
                      <strong>{ 'Upload Resi' }</strong>
                    </label>

                    <input
                      type='file'
                      className='form-control-file'
                      accept='.jpg,.jpeg,.png,.pdf'
                      disabled={ formikBag.isSubmitting || hasMissingSourceLocation }
                      onChange={
                        (event) => {
                          formikBag.setFieldValue(
                            'shipment_receipt_file',
                            event.currentTarget.files[0] || null
                          )
                        }
                      }
                    />

                    {
                      formikBag.errors.shipment_receipt_file &&
                      <div className='text-danger small mt-1'>{ formikBag.errors.shipment_receipt_file }</div>
                    }
                  </div>

                  <div className='form-group'>
                    <label>
                      <strong>{ 'Upload Bukti Pengiriman' }</strong>
                    </label>

                    <input
                      type='file'
                      className='form-control-file'
                      accept='.jpg,.jpeg,.png,.pdf'
                      disabled={ formikBag.isSubmitting || hasMissingSourceLocation }
                      onChange={
                        (event) => {
                          formikBag.setFieldValue(
                            'shipment_proof_file',
                            event.currentTarget.files[0] || null
                          )
                        }
                      }
                    />

                    {
                      formikBag.errors.shipment_proof_file &&
                      <div className='text-danger small mt-1'>{ formikBag.errors.shipment_proof_file }</div>
                    }
                  </div>
                </>
              )
            }

            {
              isReturnScheduled() &&
              (
                <>
                  <ReadOnlyField
                    label='Tracking No.'
                    value={ sellerOutboundReturn?.tracking_no }
                  />

                  <Alert color='success'>
                    { 'Consignor Return ini sudah di-ship. Jika barang sudah diterima consignor, klik Mark Received.' }
                  </Alert>
                </>
              )
            }

            {
              isReturnReceived() &&
              (
                <Alert color='success'>
                  { 'Consignor Return ini sudah diterima oleh consignor.' }
                </Alert>
              )
            }

            {
              submitError &&
              (
                <Alert color='danger'>
                  <strong>{ 'Unable to continue.' }</strong>
                  <div>{ submitError }</div>
                </Alert>
              )
            }

            <div className='d-flex justify-content-start mt-4'>
              {
                isReturnReadyForShipment() &&
                (
                  <Button
                    color='primary'
                    type='submit'
                    disabled={ formikBag.isSubmitting || hasMissingSourceLocation }
                  >
                    { formikBag.isSubmitting ? 'Processing...' : 'Ship Consignor Return' }
                  </Button>
                )
              }

              {
                isReturnScheduled() &&
                (
                  <Button
                    color='success'
                    type='button'
                    onClick={ handleReceive }
                    disabled={ formikBag.isSubmitting }
                  >
                    { 'Mark Received by Consignor' }
                  </Button>
                )
              }
            </div>
          </Form>
        )
      }
    </Formik>
  )

  return (
    <>
      <TitlePage mainTitle={ 'Consignor Return' } subTitle={ 'Detail Page' } />

      <Row>
        <Col md={ 12 }>
          <Card>
            <CardBody>
              {
                !isLoading &&
                (
                  <div className='d-flex justify-content-end mb-3'>
                    <Button
                      color='primary'
                      type='button'
                      size='sm'
                      onClick={ handleDownloadHandoverPdf }
                      disabled={ isDownloadingPdf }
                    >
                      { isDownloadingPdf ? 'Downloading...' : 'Print Handover Form' }
                    </Button>
                  </div>
                )
              }

              {
                isLoading
                  ?
                  <div className='py-4 text-center'>{ 'Loading...' }</div>
                  :
                  (
                    isProductCuratedRoute
                      ? <ProductCuratedConsignorReturnDetailForm />
                      : <OutboundConsignorReturnDetailForm />
                  )
              }
            </CardBody>
          </Card>
        </Col>
      </Row>

      <ConfirmationModal
        key={ `consignor-return-item-cancel-warning-${cancelItemWarningStep}` }
        title={ cancelItemModalTitle }
        isOpen={ isCancelItemModalOpen }
        toggle={ handleCancelItemModalToggle }
        handleConfirmation={ handleCancelItemConfirm }
        onCancel={ handleCancelItemAbort }
        reverseActions={ isShipmentItemCancel && cancelItemWarningStep === 3 }
      >
        { cancelItemModalMessage }
      </ConfirmationModal>
    </>
  )
}

SellerOutboundReturnDetailPage.propTypes = propTypes

export default SellerOutboundReturnDetailPage
